import React, { useState, useEffect, useRef } from 'react';
import SearchBox from './SearchBox';
import SearchFilters from './SearchFilters';
import SearchResults from './SearchResults';
import AdvancedFilters from '../filters/AdvancedFilters';
import TouchButton from '../ui/TouchButton';
import searchService from '../../services/SearchService';
import filterPersistence from '../../utils/filterPersistence';

const MapSearchOverlay = ({ 
  isVisible, 
  onClose, 
  onLocationSelect,
  mapCenter = null,
  mapBounds = null,
  userLocation = null,
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchFilters, setSearchFilters] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const saved = filterPersistence.loadFilters();
    return saved || filterPersistence.getDefaultFilters();
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchMode, setSearchMode] = useState('text'); // 'text' or 'map'
  const [totalResults, setTotalResults] = useState(0);

  const overlayRef = useRef(null);

  // Close overlay on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      // Focus search input
      const searchInput = document.querySelector('.map-search-overlay input');
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  const performSearch = async (query, filters = {}, reset = true) => {
    if (!query.trim() && searchMode === 'text') return;

    setIsLoading(true);
    setError(null);
    
    // Combine basic filters with advanced filters
    const combinedFilters = {
      ...filters,
      ...advancedFilters,
      centerPoint: advancedFilters.centerPoint || mapCenter || userLocation,
      distanceRadius: advancedFilters.distanceRadius
    };

    try {
      let results = [];
      let total = 0;

      if (searchMode === 'map' && mapCenter) {
        // Search in current map view with advanced filters
        const searchCenter = combinedFilters.centerPoint || mapCenter;
        const radius = combinedFilters.distanceRadius || calculateRadiusFromBounds(mapBounds) || 2000;
        
        const response = await searchService.advancedSearch({
          query: '',
          location: searchCenter,
          radius,
          ...combinedFilters,
          limit: 50
        });
        results = response.results || [];
        total = results.length;
      } else {
        // Advanced search with comprehensive filtering
        const searchCenter = combinedFilters.centerPoint || userLocation;
        const response = await searchService.advancedSearch({
          query,
          location: searchCenter,
          ...combinedFilters,
          limit: 50
        });
        results = response.results || [];
        total = response.total || 0;
      }

      // Process results with distance if user location available
      if (userLocation) {
        results = searchService.processSearchResults(results, userLocation);
        // Sort by distance
        results.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
      }

      setSearchResults(results);
      setTotalResults(total);

    } catch (err) {
      setError(err);
      console.error('Map search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setSearchMode('text');
    performSearch(query, searchFilters);
  };

  const handleMapSearch = () => {
    if (!mapCenter) return;
    setSearchMode('map');
    setSearchQuery('Locations in current view');
    performSearch('', searchFilters);
  };

  const handleFiltersChange = (filters) => {
    setSearchFilters(filters);
    if (searchQuery || searchMode === 'map') {
      performSearch(searchQuery, filters);
    }
  };

  const handleAdvancedFiltersChange = (filters) => {
    setAdvancedFilters(filters);
    // Persist filters to localStorage
    filterPersistence.saveFilters(filters);
    
    if (searchQuery || searchMode === 'map') {
      performSearch(searchQuery, searchFilters);
    }
  };

  const handleLocationSelect = (location) => {
    onLocationSelect(location);
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
    setTotalResults(0);
    setSearchMode('text');
  };

  // Get user's current location for "near me" searches
  const handleNearMeSearch = async () => {
    try {
      const location = await searchService.getCurrentLocation();
      setSearchMode('map');
      setSearchQuery('Places near me');
      
      const response = await searchService.searchNearby(location, {
        radius: 2000, // 2km radius
        ...searchFilters,
        limit: 50
      });
      
      const results = searchService.processSearchResults(response.results || [], location);
      setSearchResults(results);
      setTotalResults(results.length);
      
    } catch (error) {
      console.error('Near me search failed:', error);
      setError(error);
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`map-search-overlay ${className}`}
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '20px',
        overflowY: 'auto'
      }}
      onClick={(e) => {
        if (e.target === overlayRef.current) {
          onClose();
        }
      }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        margin: '0 auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '20px', 
            fontWeight: '600',
            color: '#333'
          }}>
            🔍 Search Locations
          </h2>
          
          <TouchButton
            onClick={onClose}
            variant="ghost"
            style={{ 
              width: '32px',
              height: '32px',
              padding: '0',
              fontSize: '18px'
            }}
          >
            ×
          </TouchButton>
        </div>

        {/* Search Section */}
        <div style={{ padding: '20px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ marginBottom: '16px' }}>
            <SearchBox
              onSearch={handleSearch}
              onLocationSelect={handleLocationSelect}
              placeholder="Search locations on map..."
              autoFocus={true}
            />
          </div>

          {/* Quick Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            {mapCenter && (
              <TouchButton
                onClick={handleMapSearch}
                variant="outline"
                size="small"
                style={{ fontSize: '12px' }}
                disabled={isLoading}
              >
                🗺️ Search This Area
              </TouchButton>
            )}
            
            <TouchButton
              onClick={handleNearMeSearch}
              variant="outline"
              size="small"
              style={{ fontSize: '12px' }}
              disabled={isLoading}
            >
              📍 Near Me
            </TouchButton>
            
            <TouchButton
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'primary' : 'outline'}
              size="small"
              style={{ fontSize: '12px' }}
            >
              ⚙️ Basic
            </TouchButton>
            
            <TouchButton
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              variant={showAdvancedFilters ? 'primary' : 'outline'}
              size="small"
              style={{ fontSize: '12px' }}
            >
              🎯 Advanced
              {filterPersistence.hasActiveFilters(advancedFilters) && (
                <span style={{ 
                  marginLeft: '4px',
                  backgroundColor: '#FF5722', 
                  color: 'white',
                  borderRadius: '50%',
                  width: '14px',
                  height: '14px',
                  fontSize: '9px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  !
                </span>
              )}
            </TouchButton>
            
            {(searchQuery || searchResults.length > 0) && (
              <TouchButton
                onClick={handleClearSearch}
                variant="ghost"
                size="small"
                style={{ fontSize: '12px', color: '#666' }}
              >
                Clear
              </TouchButton>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div style={{ marginBottom: '16px' }}>
              <SearchFilters
                onFiltersChange={handleFiltersChange}
                showLocationFilter={Boolean(userLocation)}
                userLocation={userLocation}
                initialFilters={searchFilters}
              />
            </div>
          )}
          
          {showAdvancedFilters && (
            <div style={{
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              marginBottom: '16px'
            }}>
              <AdvancedFilters
                filters={advancedFilters}
                onFiltersChange={handleAdvancedFiltersChange}
                userLocation={userLocation}
                onClose={() => setShowAdvancedFilters(false)}
              />
            </div>
          )}
        </div>

        {/* Search Results */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          padding: '20px'
        }}>
          {!searchQuery && !searchResults.length && !isLoading && (
            <SearchSuggestions
              userLocation={userLocation}
              onSearch={handleSearch}
              onLocationSelect={handleLocationSelect}
            />
          )}

          <SearchResults
            results={searchResults}
            loading={isLoading}
            error={error}
            onLocationSelect={handleLocationSelect}
            userLocation={userLocation}
            searchQuery={searchQuery}
            totalResults={totalResults}
          />
        </div>
      </div>
    </div>
  );
};

const SearchSuggestions = ({ userLocation, onSearch, onLocationSelect }) => {
  const [popularSearches, setPopularSearches] = useState([]);
  const [nearbyLocations, setNearbyLocations] = useState([]);

  useEffect(() => {
    // Load popular searches
    searchService.getPopularSearches().then(result => {
      setPopularSearches(result.popular?.slice(0, 6) || []);
    });

    // Load nearby locations if available
    if (userLocation) {
      searchService.searchNearby(userLocation, { limit: 8, radius: 1000 })
        .then(response => {
          setNearbyLocations(response.results || []);
        })
        .catch(error => {
          console.error('Failed to load nearby locations:', error);
        });
    }
  }, [userLocation]);

  return (
    <div>
      {/* Quick Search Categories */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '12px',
          color: '#333'
        }}>
          🏷️ Browse by Category
        </h3>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '8px'
        }}>
          {[
            { category: 'restaurant', label: 'Restaurants', icon: '🍽️' },
            { category: 'hotel', label: 'Hotels', icon: '🏨' },
            { category: 'attraction', label: 'Attractions', icon: '🗼' },
            { category: 'hospital', label: 'Hospitals', icon: '🏥' },
            { category: 'police', label: 'Police', icon: '🚔' },
            { category: 'pharmacy', label: 'Pharmacies', icon: '💊' }
          ].map((item) => (
            <TouchButton
              key={item.category}
              onClick={() => onSearch(item.label.toLowerCase())}
              variant="outline"
              style={{
                fontSize: '12px',
                padding: '8px',
                flexDirection: 'column',
                height: '60px',
                gap: '4px'
              }}
            >
              <span style={{ fontSize: '16px' }}>{item.icon}</span>
              <span>{item.label}</span>
            </TouchButton>
          ))}
        </div>
      </div>

      {/* Popular Searches */}
      {popularSearches.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#333'
          }}>
            🔥 Popular Searches
          </h3>
          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {popularSearches.map((search, index) => (
              <TouchButton
                key={index}
                onClick={() => onSearch(search.term)}
                variant="ghost"
                size="small"
                style={{ 
                  fontSize: '12px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}
              >
                {search.term}
              </TouchButton>
            ))}
          </div>
        </div>
      )}

      {/* Nearby Locations */}
      {nearbyLocations.length > 0 && (
        <div>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#333'
          }}>
            📍 Near You
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {nearbyLocations.slice(0, 4).map((location) => (
              <div
                key={location.id}
                onClick={() => onLocationSelect(location)}
                style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
              >
                <span style={{ fontSize: '20px' }}>
                  {getLocationIcon(location.type)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {location.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {location.formattedDistance && `${location.formattedDistance} • `}
                    {location.safetyInfo?.averageRating && 
                      `⭐ ${location.safetyInfo.averageRating.toFixed(1)}`
                    }
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Utility functions
function calculateRadiusFromBounds(bounds) {
  if (!bounds || !bounds._northEast || !bounds._southWest) return null;
  
  const ne = bounds._northEast;
  const sw = bounds._southWest;
  
  // Calculate distance between corners to estimate radius
  const distance = searchService.calculateDistance(
    { lat: sw.lat, lng: sw.lng },
    { lat: ne.lat, lng: ne.lng }
  );
  
  return Math.min(distance / 2, 10000); // Max 10km radius
}

function getLocationIcon(type) {
  const icons = {
    'restaurant': '🍽️', 'hotel': '🏨', 'attraction': '🗼',
    'transport': '🚇', 'shopping': '🛍️', 'nightlife': '🌙',
    'park': '🌳', 'beach': '🏖️', 'museum': '🏛️',
    'hospital': '🏥', 'police': '🚔', 'pharmacy': '💊'
  };
  return icons[type] || '📍';
}

export default MapSearchOverlay;